/**
 * Temporary screen-scoped design tokens for **Settings → Security Policies**.
 *
 * Mirrors the Settings design (`Settings.dc.html`, "Security Policies" section)
 * 1:1 — every colour, size, radius, shadow, spacing and type step the four
 * blocks need lives here so the components never carry a raw literal. These
 * stay screen-local on purpose: once every settings screen has been re-skinned
 * the shared values get promoted into the MUI theme and this file is deleted.
 */

import { button, contentCard, formField, inkfree, notification, signer, switchControl } from "theme/colors";

export const securityPoliciesTokens = {
  /** White rounded card chrome shared by the policy cards and the IP table. */
  card: {
    border: contentCard.borderMuted,
    borderWidth: "1px",
    radius: "14px",
    shadow: "0 1px 3px rgba(33,21,71,.04)",
    padding: "24px 28px",
    /** Gap between header block, control row and the button row. */
    gap: "16px",
  },
  /** Card heading + supporting sentence. */
  header: {
    gap: "14px",
    /** Password Expiry stacks title/description 3px apart… */
    textGap: "3px",
    /** …the MFA card uses 2px. */
    tightTextGap: "2px",
    titleColor: signer.ink,
    titleFontSize: "15px",
    titleFontWeight: 800,
    titleLetterSpacing: "-0.01em",
    descriptionColor: signer.text.meta,
    descriptionFontSize: "12.5px",
    descriptionLineHeight: 1.55,
    /** Description turns green once the policy is enforced. */
    descriptionEnforcedColor: notification.success.icon,
  },
  /** Shared app toggle chrome — see `theme/colors.ts` `switchControl`. */
  toggle: {
    width: switchControl.width,
    height: switchControl.height,
    knobSize: switchControl.knobSize,
    knobTop: `${switchControl.knobInset}px`,
    knobOffsetOff: `translateX(${switchControl.knobInset}px)`,
    knobOffsetOn: `translateX(${switchControl.knobInset + switchControl.knobTravel}px)`,
    trackOff: switchControl.trackOff,
    trackOn: switchControl.trackOn,
    knobBg: switchControl.knob,
    knobShadow: switchControl.knobShadow,
    transition: switchControl.trackTransition,
    knobTransition: switchControl.knobTransition,
    /** Optically aligns the pill with the first line of the heading. */
    marginTop: "2px",
  },
  /** Compact variant used inside an allowlist table row. */
  /**
   * Compact allowlist-row toggle — see `theme/colors.ts` `switchControl.small`.
   * Previously hardcoded `knobOffsetOn: translateX(19px)`, which overshot this
   * variant's 14px of travel (34 width - 14 knob - 2*3 inset); corrected to 17px
   * by deriving from the shared token instead.
   */
  rowToggle: {
    width: switchControl.small.width,
    height: switchControl.small.height,
    knobSize: switchControl.small.knobSize,
    knobTop: `${switchControl.small.knobInset}px`,
    knobOffsetOff: `translateX(${switchControl.small.knobInset}px)`,
    knobOffsetOn: `translateX(${switchControl.small.knobInset + switchControl.small.knobTravel}px)`,
  },
  /** "ON" / "OFF" caps label sitting left of a toggle. */
  stateLabel: {
    fontSize: "10.5px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    color: signer.text.faint,
    gap: "10px",
  },
  /** "Password will expire in [ 180 ] days" inline row. */
  expiryRow: {
    gap: "10px",
    fontSize: "12.5px",
    fontWeight: 600,
    labelWidth: 170,
    labelColor: signer.text.meta,
    suffixColor: signer.text.faint,
    suffixFontWeight: 500,
    /** Whole row + button dim while the policy is switched off. */
    disabledOpacity: 0.45,
    opacityTransition: "opacity .15s",
    errorFontSize: "10.5px",
  },
  /** Centred 72×36 day box used by the inline row. */
  expiryField: {
    width: 72,
    height: 36,
    radius: "9px",
    border: formField.border,
    bg: formField.bg,
    color: signer.ink,
    fontSize: "13px",
    fontWeight: 600,
    paddingX: "10px",
    focusBorder: signer.orange.main,
  },
  /**
   * Filled brand action ("Update", "+ Add Whitelist IP"). Colours come from the
   * global `MuiButton` `contained` override — see `button`; this screen keeps
   * the design's compact height / type step.
   */
  primaryButton: {
    ...button,
    height: 34,
    paddingX: "18px",
    fontSize: "12.5px",
    spinnerSize: 14,
    spinnerThickness: 5,
    spinnerGap: "8px",
  },
  /** Square tile holding the MFA card's leading glyph. */
  leadingIcon: {
    size: 44,
    radius: "11px",
    bg: signer.surface.muted,
    glyphSize: 20,
    glyphColor: signer.ink,
    glyphStrokeWidth: 2,
    gap: "16px",
  },
  /** "Whitelisting IPs" heading row with its two actions. */
  sectionHeader: {
    gap: "12px",
    marginTop: "4px",
    fontSize: "16px",
    fontWeight: 800,
    color: signer.ink,
    letterSpacing: "-0.01em",
  },
  /** Text-only "Activity Log" action. */
  activityLink: {
    fontSize: "12.5px",
    fontWeight: 700,
    // orange.main is 4.29:1 on the lavender page fill, so rest and hover both
    // use darker shades (6.4:1 and ~11:1, WCAG 1.4.3).
    color: signer.orange.dark,
    hoverColor: inkfree.darkest,
    gap: "6px",
    glyphSize: 13,
    glyphStrokeWidth: 2,
  },
  /** Master "Enforce IP allowlist" banner — shadowless, brand stripe on the left. */
  allowlistCard: {
    border: contentCard.borderMuted,
    stripeWidth: "3px",
    stripeColor: signer.orange.main,
    radius: "14px",
    padding: "16px 24px",
    gap: "14px",
    textGap: "2px",
    iconSize: 38,
    iconRadius: "10px",
    iconBg: signer.warn.tint,
    glyphSize: 17,
    glyphColor: signer.orange.main,
    glyphStrokeWidth: 2,
    titleFontSize: "13.5px",
    titleFontWeight: 700,
    titleColor: signer.ink,
    noteFontSize: "12.5px",
    /** Amber while every IP is allowed, green once the allowlist is enforced. */
    noteOffColor: signer.orange.dark,
    noteOnColor: notification.success.icon,
  },
  /** Search strip closing the top of the allowlist table card. */
  search: {
    padding: "16px 28px",
    borderBottom: signer.border.light,
    height: 38,
    radius: "9px",
    border: formField.border,
    bg: formField.bg,
  },
  /** Grey column strip + rows of the allowlist table. */
  table: {
    gutter: "28px",
    /** Grid template copied verbatim from the design. */
    columns: "1fr 1.2fr 150px 1.4fr 40px",
    minWidth: 720,
    headerPaddingY: "10px",
    headerBg: signer.surface.subtle,
    headerBorder: signer.border.light,
    headerFontSize: "10.5px",
    headerFontWeight: 700,
    headerLetterSpacing: "0.09em",
    headerColor: signer.text.faint,
    rowPaddingY: "14px",
    rowDivider: signer.border.divider,
    rowFontSize: "13px",
    rowColor: signer.ink,
    labelFontWeight: 600,
    valueColor: signer.text.meta,
    valueFontFamily: "inherit",
    valueFontSize: "12.5px",
    valueGap: "8px",
    dotSize: 7,
    dotOff: switchControl.trackOff,
    dotOn: notification.success.icon,
    statusGap: "8px",
    statusFontSize: "11px",
    statusFontWeight: 700,
    statusActiveColor: notification.success.icon,
    statusInactiveColor: signer.text.faint,
    addedByGap: "1px",
    addedByFontSize: "12.5px",
    addedByFontWeight: 600,
    addedOnFontSize: "11px",
    addedOnColor: signer.text.faint,
    actionColor: signer.text.faint,
    actionFontWeight: 700,
    actionLetterSpacing: "1px",
    emptyPaddingY: "34px",
    emptyFontSize: "12.5px",
    emptyColor: signer.text.meta,
    skeletonRows: 3,
    skeletonHeight: 18,
  },
  /** Inline spinner replacing a toggle while its request is in flight. */
  spinner: {
    size: 16,
    thickness: 5,
    color: signer.orange.main,
  },
  /** Placeholder blocks shown while a card's configuration loads. */
  skeleton: {
    titleWidth: 170,
    titleHeight: 18,
    descriptionHeight: 13,
    descriptionWidth: "82%",
    rowLabelWidth: 170,
    rowLabelHeight: 12,
    buttonWidth: 88,
  },
} as const;

/**
 * Phone and tablet values for **Settings → Security Policies**, traced from
 * `Settings - Mobile & Tablet.dc.html`.
 *
 * The reference draws 375, 390 and 834 identically — one column of cards, the
 * "+ Add Whitelist IP" action full width under the section heading, and every
 * allowlist entry as its own card. Applied below `md`; desktop is untouched.
 */
export const securityPoliciesResponsiveTokens = {
  card: { padding: "16px", gap: "14px" },
  header: { titleFontSize: "14.5px", descriptionFontSize: "12px" },
  expiry: { fieldHeight: 40, suffixWidth: "34px" },
  primaryButton: {
    height: "44px",
    paddingX: "20px",
    radius: "9px",
    fontSize: "13px",
  },
  /** One allowlist entry, drawn as its own card instead of a table row. */
  entryCard: {
    padding: "14px 16px",
    gap: "12px",
    textGap: "4px",
    labelFontSize: "13px",
    valueFontSize: "12px",
    addedByFontSize: "11px",
  },
} as const;

export default securityPoliciesTokens;
